import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Sparkles, TrendingUp, Star } from 'lucide-react';
import { useHomeStats } from '@/hooks/useApi.js';
import heroImage from '@/assets/homepage1.jpg';


const categories = [
  { name: 'Men', link: '/category/men' },
  { name: 'Ladies', link: '/category/ladies' },
  { name: 'Boys', link: '/category/boys' },
  { name: 'Girls', link: '/category/girls' },
];

export function HeroSection() {
  const { data: stats } = useHomeStats();

  return (
    <section className="relative min-h-[100vh] sm:min-h-[85vh] md:min-h-[90vh] overflow-hidden">
      {/* Hero Image Background */}
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="AuraKart Fashion Collection"
          className="w-full h-full object-cover object-center"
        />
        {/* Enhanced Mobile Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30 sm:from-black/60 sm:via-black/40 sm:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30 sm:from-black/50 sm:to-transparent" />
      </div>

      {/* Content Overlay */}
      <div className="relative z-10 container mx-auto px-4 h-full">
        <div className="flex items-center min-h-[100vh] sm:min-h-[85vh] md:min-h-[90vh] py-8 sm:py-12">
          <div className="max-w-full sm:max-w-2xl text-white">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.0 }}
              className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-semibold leading-[1.1] mb-4 sm:mb-6 text-balance"
            >
              Discover Your{' '}
              <span className="text-gradient-warm">Perfect</span>{' '}
              Look
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-white/95 text-base sm:text-lg md:text-xl max-w-md sm:max-w-lg mb-6 sm:mb-8 font-sans leading-relaxed"
            >
              Elevate your wardrobe with premium dresses and fashion pieces.
              Quality craftsmanship meets contemporary style.
            </motion.p>

            {/* Shop Collection CTA - Primary Focus */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-6 sm:mb-8"
            >
              <Link to="/products">
                <Button size="lg" className="w-full sm:w-auto gap-2 text-base h-14 sm:h-16 px-8 sm:px-10 bg-gradient-warm hover:opacity-90 shadow-2xl font-sans font-medium text-lg">
                  Shop Collection
                  <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6" />
                </Button>
              </Link>
            </motion.div>

            {/* Secondary Actions - Mobile Optimized */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="grid grid-cols-2 sm:flex gap-3 sm:gap-4 mb-8 sm:mb-10"
            >
              <Link to="/trending" className="w-full sm:w-auto">
                <Button variant="secondary" size="lg" className="w-full gap-2 text-sm sm:text-base h-12 px-4 sm:px-6 bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 border border-white/30 font-sans font-medium">
                  <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden xs:inline">Trending Now</span>
                  <span className="xs:hidden">Trending</span>
                </Button>
              </Link>
              <Link to="/new-arrivals" className="w-full sm:w-auto">
                <Button variant="secondary" size="lg" className="w-full gap-2 text-sm sm:text-base h-12 px-4 sm:px-6 bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 border border-white/30 font-sans font-medium">
                  <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden xs:inline">New Arrivals</span>
                  <span className="xs:hidden">New</span>
                </Button>
              </Link>
            </motion.div>

            {/* Trust Badges - Mobile Optimized */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 md:gap-8 text-white/80 font-sans"
            >
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(4)].map((_, i) => (
                    <Star key={i} className="h-3 w-3 sm:h-4 sm:w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                  <Star className="h-3 w-3 sm:h-4 sm:w-4 fill-yellow-400/50 text-yellow-400" />
                </div>
                <span className="text-xs sm:text-sm">
                  {stats?.averageRating ? `${stats.averageRating}/5` : '4.8/5'}
                  ({stats?.totalReviews ? `${stats.totalReviews}` : '1.2k+'} reviews)
                </span>
              </div>
              <div className="hidden sm:block h-4 w-px bg-white/30" />
              <span className="text-xs sm:text-sm">
                {stats?.totalCustomers ? `${stats.totalCustomers.toLocaleString()}+` : '50k+'} Happy Customers
              </span>
            </motion.div>

            {/* Mobile Stats Cards - Visible only on mobile */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="xl:hidden grid grid-cols-2 gap-3 mt-6 sm:mt-8"
            >
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/20">
                <div className="text-center text-white">
                  <div className="w-8 h-8 rounded-lg bg-gradient-warm flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="h-4 w-4 text-primary-foreground" />
                  </div>
                  <p className="text-xs text-white/70 font-sans">This Week</p>
                  <p className="font-bold text-sm font-sans">
                    {stats?.totalOrders ? `${stats.totalOrders.toLocaleString()}+` : '244+'}
                  </p>
                  <p className="text-xs text-white/70 font-sans">Orders</p>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/20">
                <div className="text-center text-white">
                  <div className="flex -space-x-1 justify-center mb-2">
                    {['👗', '👚', '🥻'].slice(0, 2).map((emoji, i) => (
                      <div
                        key={i}
                        className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs border border-white/30"
                      >
                        {emoji}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-white/70 font-sans">Latest</p>
                  <p className="font-bold text-sm font-sans">
                    {stats?.totalProducts ? `${stats.totalProducts.toLocaleString()}+` : '47+'}
                  </p>
                  <p className="text-xs text-white/70 font-sans">Designs</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Floating Stats Cards - Hidden on Mobile for Better Space */}
          <div className="hidden xl:block absolute right-8 2xl:right-16 top-1/2 -translate-y-1/2 space-y-6">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="glass-card rounded-2xl p-6 shadow-2xl backdrop-blur-md bg-white/10 border border-white/20"
            >
              <div className="text-center text-white">
                <div className="w-12 h-12 rounded-xl bg-gradient-warm flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="h-6 w-6 text-primary-foreground" />
                </div>
                <p className="text-xs text-white/70 font-sans">This Week</p>
                <p className="font-bold text-lg font-sans">
                  {stats?.totalOrders ? `${stats.totalOrders.toLocaleString()}+` : '2,500+'}
                </p>
                <p className="text-xs text-white/70 font-sans">Orders</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="glass-card rounded-2xl p-6 shadow-2xl backdrop-blur-md bg-white/10 border border-white/20"
            >
              <div className="text-center text-white">
                <div className="flex -space-x-1 justify-center mb-3">
                  {['👗', '👚', '🥻'].map((emoji, i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm border border-white/30"
                    >
                      {emoji}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-white/70 font-sans">Latest</p>
                <p className="font-bold text-lg font-sans">
                  {stats?.totalProducts ? `${stats.totalProducts.toLocaleString()}+` : '5,000+'}
                </p>
                <p className="text-xs text-white/70 font-sans">Designs</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60"
      >
        <div className="animate-bounce">
          <ArrowRight className="h-5 w-5 rotate-90" />
        </div>
      </motion.div>
    </section>
  );
}